import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import {
  fetchBalance,
  requestBalanceRefresh,
} from 'services/api/balanceService';
import { StakeLimit } from 'components/StakeLimit/StakeLimit';
import styles from '../styles/pages/BetSlip.module.css';
import { MultiBonusSlipRow } from 'components/MultiBonus/MultiBonus';
import {
  SystemPicker,
  useSlipType,
} from 'components/SystemPicker/SystemPicker';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { Navigate, useNavigate } from 'react-router-dom';
import { StartedPick } from 'components/StartedPick/StartedPick';
import classNames from 'classnames';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SlipCreationModalTypes,
  SlipInputValueTypes,
  ToastMessagesIDs,
} from 'types/enums';
import { SlipContext } from 'context/slipContext';
import { SlipEntry } from 'types/SlipEntry';
import { UserContext } from 'context/userContext';
import { marketLineSuffix, describePick } from 'helpers/pickLabel';
import { PickSummary } from 'components/PickSummary/PickSummary';
import {
  SlipPayoutNote,
  marketOddsOf,
} from 'components/PickSummary/SlipPayoutNote';
import { translateBetMarketName } from 'helpers/betI18n';
import Modal from 'react-modal';
import { MatchBetList } from 'components/mobile/modal-content/MatchBetList/MatchBetList';
import { SlipCreationModalContent } from 'components/mobile/modal-content/SlipCreationModalContent/modal-content/SlipCreationModalContent/SlipCreationModalContent';
import {
  SlipSubmitTimer,
  SLIP_SUBMIT_TIMER_MODAL_PROPS,
} from 'components/SlipSubmitTimer/SlipSubmitTimer';
import {
  approvedStakeMessage,
  slipRiskMessage,
} from 'helpers/slipRiskMessages';
import { SlipSubmittedInfo } from 'components/mobile/SlipSubmittedInfo/SlipSubmittedInfo';
import { CURRENCY } from 'App';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import {
  DEFAULT_TOTAL_STAKE,
  LIVE_TICKET_WAIT_MS,
  MAX_WIN_FOR_TICKET,
} from 'config/constants';
import {
  handleSlipInputChange,
  maxStakeFor,
  recomputeSlipValues,
  round2Decimals,
  validateInputs,
} from 'helpers/slipHelper';
import { toast } from 'react-toastify';
import { use2DecimalsNumberState } from 'hooks/use2DecimalsNumberState';
import { InfoWrapper } from 'components/mobile/InfoWrapper/InfoWrapper';
import { ShareSlip } from 'components/ShareSlip/ShareSlip';
import { BottomSheet } from 'components/mobile/BottomSheet/BottomSheet';
import { usePlayerLayout } from 'lib/playerLayout';
import type { LiveQuoteLeg } from 'services/api/slipService';
import { declineSlipCheck } from 'services/api/slipService';
import {
  hasEnabledSelection,
  pruneUnavailableLiveLegsFromEntries,
  prunedSlipMessage,
  runLiveQuoteReviewConfirmPhase,
  applyServerOddsToEntries,
  serverOddsRatio,
  runLiveQuoteVerifyPhase,
  type LiveQuoteReviewRow,
} from 'helpers/liveSlipSubmit';

const DEFAULT_STAKE = Number.parseFloat(
  process.env.REACT_APP_DEFAULT_STAKE ?? DEFAULT_TOTAL_STAKE
);

/** The slip as a page, or (modern layout) as a sheet over the page when
 *  `onClose` is given. */
const SlipFrame = ({
  onClose,
  children,
}: {
  onClose?: () => void;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  return onClose ? (
    <BottomSheet tall title={t('slip_label')} close={onClose}>
      {children}
    </BottomSheet>
  ) : (
    <MobileLayout pageTitle={t('slip_label')}>{children}</MobileLayout>
  );
};

export const BetSlip = ({ onClose }: { onClose?: () => void } = {}) => {
  const { isSystem, chooseCombination, chooseSystem } = useSlipType();
  const [stakePerBet, setStakePerBet] = use2DecimalsNumberState(DEFAULT_STAKE);
  const [totalStake, setTotalStake] = use2DecimalsNumberState(DEFAULT_STAKE);
  const [maxGain, setMaxGain] = use2DecimalsNumberState(0);
  // The ticket waits for approval: the timer says so.
  const [approvalWait, setApprovalWait] = useState(false);

  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const layout = usePlayerLayout();
  const {
    entries,
    betNumber,
    removeSlipEntry,
    maxCoefficient,
    payoutCoefficient,
    removeAllEntries,
    replaceSlipEntries,
    submitSlip,
    acceptedOddChanges,
    setAcceptedOddChanges,
  } = useContext(SlipContext);
  const { loggedUser } = useContext(UserContext);
  const [entryToDelete, setEntryToDelete] = useState<SlipEntry | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [slipCreated, setSlipCreated] = useState(false);
  // The picks of the ticket just placed: the slip is emptied as soon as the
  // ticket is accepted, and "Reuse bet slip" puts these back.
  const [placedEntries, setPlacedEntries] = useState<SlipEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStartedAt, setSubmitStartedAt] = useState<number | null>(null);
  // Set while a slip is being checked and placed: the timer shows the seconds
  // waited (the server checks a live slip for ~7 s, a prematch one at once).
  const [submitCountdownMs, setSubmitCountdownMs] = useState<number | null>(
    null
  );
  const [insufficientFundsError, setInsufficientFundsError] = useState(false);
  const [slipModalView, setSlipModalView] = useState<SlipCreationModalTypes>(
    SlipCreationModalTypes.CONFIRM_CREATING
  );
  const [quoteReviewRows, setQuoteReviewRows] = useState<LiveQuoteReviewRow[]>(
    []
  );
  const [lastVerifyLegs, setLastVerifyLegs] = useState<LiveQuoteLeg[]>([]);
  const navigate = useNavigate();

  // The stake per bet stays when the number of bets changes, so the total
  // follows it; the potential win follows both.
  useEffect(() => {
    const total = betNumber > 1 ? stakePerBet * betNumber : totalStake;
    if (betNumber > 1) setTotalStake(total);
    setMaxGain(total * payoutCoefficient);
  }, [betNumber, payoutCoefficient]);

  // While a field is typed in, the others follow it at once.
  const [editingGain, setEditingGain] = useState(false);
  const liveRecompute = (version: SlipInputValueTypes, raw: string) =>
    recomputeSlipValues(
      version,
      Number.parseFloat(raw),
      { betNumber, maxCoefficient: payoutCoefficient },
      { setTotalStake, setStakePerBet, setMaxGain }
    );

  const handleInputChange = (event: any, version: SlipInputValueTypes) => {
    const newValue = round2Decimals(event.target.value);
    try {
      handleSlipInputChange(
        version,
        newValue,
        {
          betNumber,
          maxCoefficient: payoutCoefficient,
        },
        { setTotalStake, setStakePerBet, setMaxGain },
        t
      );
    } catch (e: any) {
      toast(t(e.message), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      if (betNumber > 1) {
        setStakePerBet(DEFAULT_STAKE);
        setTotalStake(DEFAULT_STAKE * betNumber);
        setMaxGain(DEFAULT_STAKE * maxCoefficient);
      } else {
        setTotalStake(DEFAULT_STAKE);
        setMaxGain(DEFAULT_STAKE * payoutCoefficient);
      }
    }
  };

  const preventMinusSign = (event: any) => {
    if (event.code === 'Minus' || event.code === 'KeyE') {
      event.preventDefault();
    }
  };

  const validatePaste = (event: any) => {
    const clipboardData = event.clipboardData;
    const pastedData = Number.parseFloat(clipboardData.getData('text'));
    if (Number.isNaN(pastedData) || pastedData < 0) {
      event.preventDefault();
    }
  };

  const handleRemoveClick = (entry: SlipEntry) => {
    if (entry.bets.length === 1) {
      removeSlipEntry(entry.match);
    } else {
      setEntryToDelete(entry);
      setDeleteModalOpen(true);
    }
  };

  const closeEntryDeleteModal = () => {
    setEntryToDelete(null);
    setDeleteModalOpen(false);
  };

  const resetSlipSubmitModal = () => {
    setSlipCreated(false);
    setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    setQuoteReviewRows([]);
    setLastVerifyLegs([]);
  };

  const handleSubmitBetClick = () => {
    // Only started (or suspended) picks left: nothing to place.
    if (!hasEnabledSelection(entries)) {
      toast(t('slip_no_active_picks_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      return;
    }
    try {
      validateInputs(
        totalStake,
        betNumber > 1 ? stakePerBet : undefined,
        SlipInputValueTypes.TOTAL_STAKE,
        t
      );
      resetSlipSubmitModal();
      setSubmitModalOpen(true);
    } catch (e: any) {
      toast(e.message, {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
    }
  };

  // Submits straight away. The wait before a live ticket is the server's quote
  // check (runLiveQuoteVerifyPhase); a prematch slip has nothing to wait for.
  const executeCreateTicket = async () => {
    setIsLoading(true);
    if (insufficientFundsError) {
      setInsufficientFundsError(false);
    }
    const result = await submitSlip(
      totalStake,
      loggedUser,
      (seconds: number) => {
        setApprovalWait(true);
        setSubmitStartedAt(Date.now());
        setSubmitCountdownMs(seconds * 1000);
      }
    );
    setApprovalWait(false);
    setIsLoading(false);
    const riskMessage = slipRiskMessage(result, t);
    if (result.ok && result.approvedStake) {
      toast(approvedStakeMessage(result.approvedStake, t), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
    }
    if (!result.ok && riskMessage) {
      toast(riskMessage, { toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR });
      // The largest stake still taken goes into the stake box, so the
      // player only confirms again.
      if (
        result.code === 'risk_max_stake' &&
        result.maxStake &&
        betNumber <= 1
      ) {
        setTotalStake(result.maxStake);
        setMaxGain(result.maxStake * payoutCoefficient);
      }
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (result.ok) {
      setPlacedEntries(entries);
      removeAllEntries();
      setSlipCreated(true);
      setSubmitModalOpen(false);
      setSlipModalView(SlipCreationModalTypes.SLIP_CREATED);
    } else if (result.insufficientFunds) {
      setSubmitModalOpen(false);
      setInsufficientFundsError(true);
    } else if (/Same ticket already placed/i.test(result.error)) {
      toast(t('duplicate_ticket_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (/could not be confirmed/i.test(result.error)) {
      toast(t('quotes_unconfirmed_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (/odd not found in live feed/i.test(result.error)) {
      toast(t('live_quote_selection_unavailable'), {
        toastId: ToastMessagesIDs.LIVE_SLIP_SELECTION_REMOVED,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (/Potential win amount is greater than/i.test(result.error)) {
      toast(t('max_win_exceeded_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else {
      setSubmitModalOpen(false);
      toast(result.error, {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      resetSlipSubmitModal();
    }
  };

  // The provider's prices pushed a slip that was within the ticket maximum
  // over it: stop and say so, rather than place it with the win capped.
  const stopIfOddsPushedOverLimit = (legs: LiveQuoteLeg[]): boolean => {
    const ratio = serverOddsRatio(legs);
    const before = totalStake * payoutCoefficient;
    if (before > MAX_WIN_FOR_TICKET || before * ratio <= MAX_WIN_FOR_TICKET) {
      return false;
    }
    toast(
      t('odds_changed_over_limit_message', {
        amount: maxStakeFor(payoutCoefficient * ratio).toFixed(2),
        currency: CURRENCY,
      }),
      { toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR }
    );
    setSubmitModalOpen(false);
    resetSlipSubmitModal();
    return true;
  };

  // keepTrying: the player said yes to the tries after a suspended price, so
  // a try with no answer simply goes on to the next, each with its own count,
  // until the server says none are left.
  const onConfirmSubmitFirstStep = async (keepTrying = false) => {
    // Not enough money: said at once, not after the provider check's
    // countdown (the server still checks it when the ticket is written).
    // The balance on screen may be behind (a deposit just made): asked
    // afresh before saying no.
    if (
      loggedUser &&
      Number.isFinite(Number(loggedUser.balance)) &&
      Number(loggedUser.balance) + 0.001 < totalStake
    ) {
      const fresh = await fetchBalance();
      if (!fresh || fresh.balance + 0.001 < totalStake) {
        if (fresh) requestBalanceRefresh();
        setSubmitModalOpen(false);
        setInsufficientFundsError(true);
        return;
      }
      requestBalanceRefresh();
    }
    setSubmitStartedAt(Date.now());
    // Every slip is checked with the provider first (prematch too), which can
    // take a few seconds, so the timer shows for all of them.
    setSubmitCountdownMs(LIVE_TICKET_WAIT_MS);
    setIsLoading(true);
    const phase = await runLiveQuoteVerifyPhase(entries, acceptedOddChanges, t);
    if (phase.kind === 'quote_suspended' && keepTrying) {
      await onConfirmSubmitFirstStep(true);
      return;
    }
    setIsLoading(false);
    if (phase.kind === 'error') {
      toast(t(phase.toastKey), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      return;
    }
    if (phase.kind === 'quote_suspended') {
      setSlipModalView(SlipCreationModalTypes.QUOTE_SUSPENDED);
      return;
    }
    if (phase.kind === 'live_unavailable_prune') {
      const { nextEntries, removedTitles, startedTitles } =
        pruneUnavailableLiveLegsFromEntries(entries, phase.legs);
      replaceSlipEntries(nextEntries);
      toast(prunedSlipMessage(t, removedTitles, startedTitles), {
        toastId: ToastMessagesIDs.LIVE_SLIP_SELECTION_REMOVED,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      setQuoteReviewRows([]);
      setLastVerifyLegs([]);
      if (!hasEnabledSelection(nextEntries)) {
        setSubmitModalOpen(false);
        resetSlipSubmitModal();
      }
      return;
    }
    if (phase.kind === 'quote_review') {
      setLastVerifyLegs(phase.legs);
      setQuoteReviewRows(phase.rows);
      setSlipModalView(SlipCreationModalTypes.LIVE_QUOTE_REVIEW);
      return;
    }
    const legs = phase.kind === 'no_live' ? [] : phase.legs;
    setLastVerifyLegs(legs);
    // Show the prices the ticket is written at, so the slip's potential win
    // matches the server's.
    const priced = applyServerOddsToEntries(entries, legs);
    if (priced) replaceSlipEntries(priced);
    if (stopIfOddsPushedOverLimit(legs)) return;
    await executeCreateTicket();
  };

  const onConfirmQuoteReviewSubmit = async () => {
    setSubmitStartedAt(Date.now());
    setSubmitCountdownMs(LIVE_TICKET_WAIT_MS);
    setIsLoading(true);
    const phase = await runLiveQuoteReviewConfirmPhase(
      entries,
      acceptedOddChanges,
      lastVerifyLegs,
      t
    );
    setIsLoading(false);
    if (phase.kind === 'error') {
      toast(t(phase.toastKey), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      return;
    }
    if (phase.kind === 'quote_suspended') {
      setSlipModalView(SlipCreationModalTypes.QUOTE_SUSPENDED);
      return;
    }
    if (phase.kind === 'live_unavailable_prune') {
      const { nextEntries, removedTitles, startedTitles } =
        pruneUnavailableLiveLegsFromEntries(entries, phase.legs);
      replaceSlipEntries(nextEntries);
      toast(prunedSlipMessage(t, removedTitles, startedTitles), {
        toastId: ToastMessagesIDs.LIVE_SLIP_SELECTION_REMOVED,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      setQuoteReviewRows([]);
      setLastVerifyLegs([]);
      if (!hasEnabledSelection(nextEntries)) {
        setSubmitModalOpen(false);
        resetSlipSubmitModal();
      }
      return;
    }
    if (phase.kind === 'quote_review_still') {
      setLastVerifyLegs(phase.legs);
      setQuoteReviewRows(phase.rows);
      return;
    }
    setLastVerifyLegs(phase.legs);
    const priced = applyServerOddsToEntries(entries, phase.legs);
    if (priced) replaceSlipEntries(priced);
    if (stopIfOddsPushedOverLimit(phase.legs)) return;
    await executeCreateTicket();
  };

  const closeSubmitModalOnly = () => {
    setSubmitModalOpen(false);
    resetSlipSubmitModal();
  };

  const clearSlip = () => {
    removeAllEntries();
    setSubmitModalOpen(false);
    resetSlipSubmitModal();
  };

  const reuseSlip = () => {
    replaceSlipEntries(placedEntries);
    setPlacedEntries([]);
    setSlipCreated(false);
    setSubmitModalOpen(false);
    resetSlipSubmitModal();
  };

  const submitModalConfirmHandler =
    slipModalView === SlipCreationModalTypes.LIVE_QUOTE_REVIEW
      ? onConfirmQuoteReviewSubmit
      : () =>
          onConfirmSubmitFirstStep(
            slipModalView === SlipCreationModalTypes.QUOTE_SUSPENDED
          );

  const submitModalRejectHandler = () => {
    if (slipModalView === SlipCreationModalTypes.QUOTE_SUSPENDED) {
      declineSlipCheck();
    }
    closeSubmitModalOnly();
  };

  if (!isMobile) {
    return <Navigate to="/not-found" />;
  }

  if (slipCreated) {
    return (
      <SlipFrame onClose={onClose}>
        <SlipSubmittedInfo onClose={clearSlip} onReuse={reuseSlip} />
      </SlipFrame>
    );
  }

  if (entries.length === 0)
    return (
      <SlipFrame onClose={onClose}>
        <EmptySlip />
      </SlipFrame>
    );

  if (insufficientFundsError) {
    return (
      <SlipFrame onClose={onClose}>
        <InsufficientFundsMessage
          onClose={() => {
            setInsufficientFundsError(false);
            resetSlipSubmitModal();
          }}
        />
      </SlipFrame>
    );
  }

  return (
    <SlipFrame onClose={onClose}>
      <div className={styles.wrapper}>
        <div
          className={classNames(styles.slipTypeButton, {
            [styles['selected']]: !isSystem,
          })}
          onClick={chooseCombination}
        >
          <div className={styles.checkmark} />
          {t('combination_label')}
        </div>
        <div
          className={classNames(styles.slipTypeButton, {
            [styles['selected']]: isSystem,
          })}
          onClick={chooseSystem}
        >
          <div
            className={classNames(styles.checkmark, styles.iconPositionRight)}
          />
          {t('system_label')}
        </div>
      </div>
      <SystemPicker />
      <div className={styles.slipTools}>
        {layout === 'modern' && <ShareSlip />}
        {(entries.length > 1 || entries[0].bets.length > 1) && (
          <div className={styles.removeAll} onClick={removeAllEntries}>
            {t('remove_all_bets_label')}
            <div className={styles.removeIcon} />
          </div>
        )}
      </div>
      {entries.map((x: SlipEntry, index: number) => (
        <div className={styles.betEntry} key={index}>
          <div className={styles.teams}>
            {/* Tapping the match opens its whole offer, to swap a pick. */}
            <span
              role="button"
              tabIndex={0}
              onClick={() =>
                navigate(
                  x.isLive
                    ? '/live-betting'
                    : `/sports-betting/match/${x.match.fi}`
                )
              }
            >
              {x.match.home?.name ??
                (typeof x.match.league === 'string'
                  ? x.match.league
                  : x.match.league?.name) ??
                'Unknown'}
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={() =>
                navigate(
                  x.isLive
                    ? '/live-betting'
                    : `/sports-betting/match/${x.match.fi}`
                )
              }
            >
              {x.match.away?.name ?? '-'}
            </span>
            <div
              className={styles.removeMatch}
              onClick={() => handleRemoveClick(x)}
            >
              <ThemeIcon icon="delete" alt="Clear" variant="inline" />
            </div>
          </div>
          {x.bets.map((bet, index) => (
            <div key={index}>
              <div className={styles.bet} data-is-disabled={bet.disabled}>
                <PickSummary
                  info={{
                    ...describePick(
                      {
                        betName: bet.bet.name,
                        odd: bet.odd,
                        home: x.match.home?.name,
                        away: x.match.away?.name,
                        marketOdds: marketOddsOf(x, bet.bet.name),
                      },
                      t
                    ),
                    market:
                      translateBetMarketName(bet.bet, t) +
                      marketLineSuffix(bet.bet.name, bet.odd),
                  }}
                  odds={bet.odd.odds}
                  pickClassName={styles.oddName}
                  match={`${x.match.home?.name ?? ''} - ${x.match.away?.name ?? ''}`}
                />
                <span>{bet.odd.odds.toFixed(2)}</span>
              </div>
              {bet.started && <StartedPick fi={x.match.fi} />}
            </div>
          ))}
        </div>
      ))}
      <StakeLimit
        totalStake={totalStake}
        maxCoefficient={payoutCoefficient}
        onStakeMax={(stake) =>
          handleInputChange(
            { target: { value: stake } },
            SlipInputValueTypes.TOTAL_STAKE
          )
        }
      />
      <div className={styles.slipDetails}>
        <span>{t('number_of_events_label')}</span>
        <span>{entries.length}</span>
        {betNumber > 1 && (
          <>
            <span>{t('number_of_bets_label')}</span>
            <span>{betNumber}</span>
            <span>{t('stake_per_bet_label')}</span>
            <span>
              <input
                type="number"
                min={0.01}
                step={0.01}
                value={stakePerBet ? stakePerBet : ''}
                onBlur={(event) =>
                  handleInputChange(event, SlipInputValueTypes.STAKE_PER_BET)
                }
                onChange={(event) =>
                  liveRecompute(
                    SlipInputValueTypes.STAKE_PER_BET,
                    event.target.value
                  )
                }
                onKeyDown={preventMinusSign}
                onPaste={validatePaste}
              />
            </span>
          </>
        )}
        <span>{t('total_stake_label')}</span>
        <span>
          <input
            type="number"
            step={0.01}
            min="0.01"
            maxLength={16}
            value={totalStake ? totalStake : ''}
            onChange={(event) =>
              liveRecompute(SlipInputValueTypes.TOTAL_STAKE, event.target.value)
            }
            onKeyDown={preventMinusSign}
            onPaste={validatePaste}
            onBlur={(event) =>
              handleInputChange(event, SlipInputValueTypes.TOTAL_STAKE)
            }
          />
        </span>
        <span>{t('max_coefficient_label')}</span>
        <span>{maxCoefficient.toFixed(2)}</span>
        <span>{t('max_gain_label')}</span>
        <span>
          <input
            type="number"
            min="0"
            maxLength={16}
            value={
              maxGain
                ? editingGain
                  ? maxGain
                  : Math.min(maxGain, MAX_WIN_FOR_TICKET)
                : ''
            }
            onChange={(event) =>
              liveRecompute(SlipInputValueTypes.MAX_GAIN, event.target.value)
            }
            onKeyDown={preventMinusSign}
            onPaste={validatePaste}
            step={0.01}
            onBlur={(event) => {
              setEditingGain(false);
              handleInputChange(event, SlipInputValueTypes.MAX_GAIN);
            }}
            onFocus={() => setEditingGain(true)}
          />
        </span>
        <span className={styles.payment}>{t('possible_payout_label')}</span>
        <span className={styles.payment}>
          {maxGain ? Math.min(maxGain, MAX_WIN_FOR_TICKET).toFixed(2) : 0}{' '}
          {CURRENCY}
        </span>
        {/* A system plays no bonus on the number of matches. */}
        {!isSystem && (
          <MultiBonusSlipRow
            entries={entries}
            win={maxGain ? Math.min(maxGain, MAX_WIN_FOR_TICKET) : 0}
            maxWin={MAX_WIN_FOR_TICKET}
          />
        )}
        <SlipPayoutNote
          className={styles.slipNote}
          bets={betNumber}
          system={isSystem}
        />
      </div>
      <div className={styles.buttonsWrapper}>
        <div
          className={classNames(styles.button, {
            [styles['acceptedChanges']]: acceptedOddChanges,
          })}
          onClick={() => setAcceptedOddChanges(!acceptedOddChanges)}
        >
          <div
            className={classNames(styles.checkmark, styles.iconPositionRight)}
          />
          {t('accept_odds_change_label')}
        </div>
      </div>
      <div className={styles.buttonsWrapper}>
        {loggedUser ? (
          <div
            className={classNames(styles.button, styles.submitButton)}
            onClick={handleSubmitBetClick}
          >
            {t('submit_bet_label')}
            <div
              className={classNames(styles.checkmark, styles.iconPositionRight)}
            />
          </div>
        ) : (
          <div
            className={classNames(styles.button, styles.loginButton)}
            onClick={() => navigate('/login?from=/bet-slip')}
          >
            {t('login_label')}
            <div className={styles.keylock} />
          </div>
        )}
      </div>
      <Modal
        isOpen={deleteModalOpen && !!entryToDelete}
        ariaHideApp={false}
        contentLabel="All Bets for Event"
        className={styles.modal}
      >
        {entryToDelete && (
          <MatchBetList
            entry={entryToDelete!}
            closeModal={closeEntryDeleteModal}
          />
        )}
      </Modal>
      <Modal
        isOpen={submitModalOpen}
        ariaHideApp={false}
        contentLabel="Submit bet slip"
        className={styles.modal}
        {...(isLoading ? SLIP_SUBMIT_TIMER_MODAL_PROPS : {})}
      >
        {isLoading && submitCountdownMs != null && (
          <SlipSubmitTimer
            key={submitStartedAt}
            startedAt={submitStartedAt}
            seconds={Math.ceil(submitCountdownMs / 1000)}
            title={approvalWait ? t('approval_pending_title') : undefined}
            hint={approvalWait ? t('approval_pending_hint') : undefined}
          />
        )}
        {!isLoading && (
          <SlipCreationModalContent
            modalType={slipModalView}
            onReject={submitModalRejectHandler}
            onConfirm={submitModalConfirmHandler}
            liveQuoteReviewRows={quoteReviewRows}
          />
        )}
      </Modal>
    </SlipFrame>
  );
};

const EmptySlip = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={styles.emptySlipWrapper}>
      <div className={styles.infoWrapper}>
        <ThemeIcon icon="info-circle" alt="Info" variant="inline" />
        <div className={styles.emptySlipMessage}>{t('empty_slip_message')}</div>
      </div>
      <div className={styles.emptySlipButtons}>
        <div onClick={() => navigate('/sports-betting')}>
          {t('sports_betting_label')} <div className={styles.rightArrow} />
        </div>
        <div onClick={() => navigate('/live-betting')}>
          {t('live_betting_label')}
          <div className={styles.rightArrow} />
        </div>
      </div>
    </div>
  );
};

interface InsufficientFundsMessageProps {
  onClose: () => void;
}

const InsufficientFundsMessage = ({
  onClose,
}: InsufficientFundsMessageProps) => {
  const { t } = useTranslation();
  return (
    <InfoWrapper onClose={onClose}>
      <div className={styles.insufficientFundsMessage}>
        <ThemeIcon icon="info-circle" alt="Info" variant="inline" />{' '}
        {t('insufficient_funds_error_message')}
      </div>
    </InfoWrapper>
  );
};
